import { create } from "zustand";
import type { AuthUser, AuthTokens } from "@career-ops-amplify/shared";

const SESSION_KEY = "career-ops-auth";

/**
 * Persist auth in localStorage so multi-tab sessions stay signed in.
 * SPA API calls must still send Authorization: Bearer (see api/client.ts).
 */
function loadSession(): { user: AuthUser | null; tokens: AuthTokens | null } {
  try {
    const raw =
      localStorage.getItem(SESSION_KEY) ?? sessionStorage.getItem(SESSION_KEY);
    if (!raw) return { user: null, tokens: null };
    const parsed = JSON.parse(raw);
    // Basic expiry check using expiresIn (seconds from creation)
    if (parsed.tokens?.expiresIn) {
      const saved = parsed._savedAt ?? 0;
      if (Date.now() - saved > parsed.tokens.expiresIn * 1000) {
        localStorage.removeItem(SESSION_KEY);
        sessionStorage.removeItem(SESSION_KEY);
        return { user: null, tokens: null };
      }
    }
    // Migrate legacy sessionStorage → localStorage for multi-tab
    if (!localStorage.getItem(SESSION_KEY) && sessionStorage.getItem(SESSION_KEY)) {
      localStorage.setItem(SESSION_KEY, raw);
      sessionStorage.removeItem(SESSION_KEY);
    }
    return { user: parsed.user ?? null, tokens: parsed.tokens ?? null };
  } catch {
    localStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(SESSION_KEY);
    return { user: null, tokens: null };
  }
}

function persistSession(
  user: AuthUser | null,
  tokens: AuthTokens | null,
) {
  if (!user || !tokens) {
    localStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(SESSION_KEY);
    return;
  }
  localStorage.setItem(
    SESSION_KEY,
    JSON.stringify({ user, tokens, _savedAt: Date.now() }),
  );
  sessionStorage.removeItem(SESSION_KEY);
}

interface AuthState {
  user: AuthUser | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  initialized: boolean;

  signIn: (user: AuthUser, tokens: AuthTokens) => void;
  signUp: (user: AuthUser, tokens: AuthTokens) => void;
  signOut: () => void;
  setSession: (user: AuthUser, tokens: AuthTokens) => void;
  initialize: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  tokens: null,
  isAuthenticated: false,
  initialized: false,

  signIn: (user, tokens) => {
    persistSession(user, tokens);
    set({ user, tokens, isAuthenticated: true });
  },

  signUp: (user, tokens) => {
    persistSession(user, tokens);
    set({ user, tokens, isAuthenticated: true });
  },

  signOut: () => {
    persistSession(null, null);
    set({ user: null, tokens: null, isAuthenticated: false });
  },

  setSession: (user, tokens) => {
    persistSession(user, tokens);
    set({ user, tokens, isAuthenticated: true });
  },

  initialize: () => {
    const { user, tokens } = loadSession();
    set({
      user,
      tokens,
      isAuthenticated: user !== null && tokens !== null,
      initialized: true,
    });
  },
}));

export function getAccessToken(): string | null {
  return useAuthStore.getState().tokens?.accessToken ?? null;
}